import pick from 'lodash/pick';
import type {
  ArdoqId,
  ClaimExecutionStatus,
  FutureStateClaim,
  RenderClaimType,
  RenderCompletion,
  RenderEntityType,
  RenderEvaluation,
  RenderOp,
  RenderReason,
  RenderStatus,
} from '@ardoq/api-types';
import {
  remapClaimIds,
  toDraftAliasOf,
  type DraftAlias,
  type DraftKind,
  type FutureStateDrafts,
  type MaybeDrafted,
} from 'transformations/futureStatePage/futureStateDrafts';
import type { RenderResult } from 'transformations/futureStatePage/futureStateRenderResult$';
import { isCurrentRejection, type RejectedBatch } from './rejectedBatch$';

type ContextEvaluation = {
  status: RenderStatus;
  op: RenderOp;
  reason: RenderReason | null;
  conflictingClaimAliases: DraftAlias[];
};

export type ContextClaim = {
  alias: DraftAlias;
  draft: DraftKind | null;
  claimType: RenderClaimType;
  entityType: RenderEntityType;
  targetId: string | null;
  claimGroupAlias: DraftAlias | null;
  assertProperties: Record<string, unknown> | null;
  evaluation: ContextEvaluation | null;
};

export type ContextClaimGroup = {
  alias: DraftAlias;
  draft: DraftKind | null;
  name: string;
  description: string | null;
};

type ContextRender =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; renderedDraftVersion: number };

export type FutureStateDraftCommandContext = {
  futureStateId: ArdoqId;
  render: ContextRender;
  rejectedBatch: { reason: string } | null;
  claimGroups: ContextClaimGroup[];
  claims: ContextClaim[];
  completion: RenderCompletion | null;
  claimExecutionStatus: ClaimExecutionStatus | null;
};

const toContextClaim = (
  merged: MaybeDrafted<FutureStateClaim>,
  evaluation: RenderEvaluation | undefined,
  toAlias: (id: ArdoqId) => DraftAlias
): ContextClaim => {
  const claim = remapClaimIds(merged, toAlias);
  return {
    alias: claim._id,
    draft: claim.draftKind ?? null,
    ...pick(claim, ['claimType', 'entityType']),
    targetId: claim.targetId ?? null,
    claimGroupAlias: claim.claimGroupId ?? null,
    assertProperties: claim.assertProperties ?? null,
    evaluation:
      evaluation && claim.draftKind !== 'delete'
        ? {
            ...pick(evaluation, ['status', 'op', 'reason']),
            conflictingClaimAliases: (evaluation.conflictingClaims ?? []).map(
              toAlias
            ),
          }
        : null,
  };
};

const toRejectedBatch = (
  rejected: RejectedBatch | null,
  drafts: FutureStateDrafts
): FutureStateDraftCommandContext['rejectedBatch'] =>
  isCurrentRejection(rejected, drafts) ? { reason: rejected.reason } : null;

export const toFutureStateDraftCommandContext = (
  renderResult: RenderResult,
  drafts: FutureStateDrafts,
  rejected: RejectedBatch | null
): FutureStateDraftCommandContext => {
  const { futureStateId, draftIdByAlias } = drafts;
  const rejectedBatch = toRejectedBatch(rejected, drafts);
  const pending = {
    futureStateId,
    rejectedBatch,
    claimGroups: [],
    claims: [],
    completion: null,
    claimExecutionStatus: null,
  };
  if (renderResult.kind === 'loading' || renderResult.kind === 'error') {
    return { ...pending, render: { status: renderResult.kind } };
  }
  if (renderResult.futureStateId !== futureStateId) {
    return { ...pending, render: { status: 'loading' } };
  }
  const toAlias = toDraftAliasOf(draftIdByAlias);
  const response = renderResult.kind === 'ready' ? renderResult.response : null;
  return {
    futureStateId,
    render: {
      status: 'ready',
      renderedDraftVersion: renderResult.renderedDraftVersion,
    },
    rejectedBatch,
    claimGroups: renderResult.claimGroups.map(claimGroup => ({
      alias: toAlias(claimGroup._id),
      draft: claimGroup.draftKind ?? null,
      name: claimGroup.name,
      description: claimGroup.description ?? null,
    })),
    claims: renderResult.claims.map(claim =>
      toContextClaim(claim, response?.evaluations[claim._id], toAlias)
    ),
    completion: response?.completion ?? null,
    claimExecutionStatus: response?.claimExecution.status ?? null,
  };
};
